import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Divider,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatCard from '../components/common/StatCard';
import StatusBadge from '../components/common/StatusBadge';
import { getLeadReport, getDisbursementReport, getSlaReport } from '../services/reportService';
import { STATUSES } from '../config/constants';

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 2 }}>
    {value === index && children}
  </Box>
);

const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

// -- Lead Report Columns --
const leadColumns = [
  { field: 'fileNumber', headerName: 'File #', width: 120 },
  {
    field: 'studentName',
    headerName: 'Student',
    flex: 1,
    minWidth: 160,
    valueGetter: (value, row) => row.studentName || `${row.firstName || ''} ${row.lastName || ''}`.trim(),
  },
  { field: 'source', headerName: 'Source', width: 120 },
  { field: 'country', headerName: 'Country', width: 130 },
  { field: 'bankName', headerName: 'Bank', width: 140 },
  {
    field: 'loanAmount',
    headerName: 'Loan Amount',
    type: 'number',
    width: 140,
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 170,
    renderCell: (params) => <StatusBadge status={params.value} />,
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 130,
    valueFormatter: (value) =>
      value ? new Date(value).toLocaleDateString('en-IN') : '-',
  },
];

// -- Disbursement Report Columns --
const disbursementColumns = [
  { field: 'fileNumber', headerName: 'File #', width: 120 },
  { field: 'studentName', headerName: 'Student', flex: 1, minWidth: 160 },
  { field: 'bankName', headerName: 'Bank', width: 140 },
  {
    field: 'sanctionedAmount',
    headerName: 'Sanctioned',
    type: 'number',
    width: 140,
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: 'disbursedAmount',
    headerName: 'Disbursed',
    type: 'number',
    width: 140,
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: 'disbursedDate',
    headerName: 'Disbursed Date',
    width: 140,
    valueFormatter: (value) =>
      value ? new Date(value).toLocaleDateString('en-IN') : '-',
  },
];

// -- SLA Report Columns --
const slaColumns = [
  { field: 'fileNumber', headerName: 'File #', width: 120 },
  { field: 'studentName', headerName: 'Student', flex: 1, minWidth: 160 },
  { field: 'bankName', headerName: 'Bank', width: 140 },
  {
    field: 'status',
    headerName: 'Status',
    width: 170,
    renderCell: (params) => <StatusBadge status={params.value} />,
  },
  {
    field: 'daysInCurrentStatus',
    headerName: 'Days in Status',
    type: 'number',
    width: 130,
    align: 'center',
    headerAlign: 'center',
  },
  {
    field: 'totalDays',
    headerName: 'Total Days',
    type: 'number',
    width: 120,
    align: 'center',
    headerAlign: 'center',
  },
  {
    field: 'slaBreached',
    headerName: 'SLA Breached',
    width: 130,
    renderCell: (params) => (
      <Typography
        variant="body2"
        fontWeight={600}
        color={params.value ? 'error.main' : 'success.main'}
      >
        {params.value ? 'Yes' : 'No'}
      </Typography>
    ),
  },
];

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  ...Object.values(STATUSES).map((s) => ({ label: s, value: s })),
];

const ReportsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState({});

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [bankFilter, setBankFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const getFilters = useCallback(() => {
    const params = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (bankFilter) params.bank = bankFilter;
    if (countryFilter) params.country = countryFilter;
    if (statusFilter) params.status = statusFilter;
    return params;
  }, [dateFrom, dateTo, bankFilter, countryFilter, statusFilter]);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const params = getFilters();
      let response;

      switch (tabValue) {
        case 0:
          response = await getLeadReport(params);
          break;
        case 1:
          response = await getDisbursementReport(params);
          break;
        case 2:
          response = await getSlaReport(params);
          break;
        default:
          return;
      }

      const result = response.data.data;
      const data = result?.records || result?.report || result || [];
      setReportData(Array.isArray(data) ? data : []);
      setSummary(result?.summary || {});
    } catch (err) {
      console.error('Failed to fetch report:', err);
      setReportData([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  }, [tabValue, getFilters]);

  useEffect(() => {
    fetchReport();
  }, [tabValue, fetchReport]);

  const handleApplyFilters = () => {
    fetchReport();
  };

  const handleResetFilters = () => {
    setDateFrom('');
    setDateTo('');
    setBankFilter('');
    setCountryFilter('');
    setStatusFilter('');
  };

  const getColumns = () => {
    switch (tabValue) {
      case 0:
        return leadColumns;
      case 1:
        return disbursementColumns;
      case 2:
        return slaColumns;
      default:
        return leadColumns;
    }
  };

  const rows = reportData.map((item, index) => ({
    id: item._id || index,
    ...item,
  }));

  return (
    <Box>
      <PageHeader title="Reports" />

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Lead Report" />
          <Tab label="Disbursement Report" />
          <Tab label="SLA Report" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Bank"
              placeholder="Bank name"
              value={bankFilter}
              onChange={(e) => setBankFilter(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Country"
              placeholder="Country"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" size="small" onClick={handleApplyFilters}>
                Apply
              </Button>
              <Button variant="outlined" size="small" onClick={handleResetFilters}>
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Stats */}
      {Object.keys(summary).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            {summary.totalRecords !== undefined && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard title="Total Records" value={summary.totalRecords} />
              </Grid>
            )}
            {summary.totalAmount !== undefined && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard title="Total Amount" value={formatCurrency(summary.totalAmount)} />
              </Grid>
            )}
            {summary.approved !== undefined && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard title="Approved" value={summary.approved} />
              </Grid>
            )}
            {summary.slaBreached !== undefined && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard title="SLA Breached" value={summary.slaBreached} />
              </Grid>
            )}
            {summary.disbursedAmount !== undefined && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard title="Disbursed Amount" value={formatCurrency(summary.disbursedAmount)} />
              </Grid>
            )}
            {summary.avgDays !== undefined && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard title="Avg. Days" value={`${summary.avgDays} days`} />
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Data Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Paper sx={{ p: 2 }}>
          <DataGrid
            rows={rows}
            columns={getColumns()}
            initialState={{
              pagination: { paginationModel: { pageSize: 15 } },
            }}
            pageSizeOptions={[15, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'grey.50',
              },
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default ReportsPage;
